import type { PreferenceStore, StoredPreferences } from "./types.js";

export const DEFAULT_PREFERENCE_KEY = "miko_privacy_preferences";

function isStoredPreferences(value: unknown): value is StoredPreferences {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<StoredPreferences>;
  return (
    candidate.schemaVersion === 1 &&
    typeof candidate.siteId === "string" &&
    typeof candidate.policyVersion === "string" &&
    typeof candidate.updatedAt === "string" &&
    typeof candidate.expiresAt === "string" &&
    !!candidate.decisions &&
    typeof candidate.decisions === "object"
  );
}

export class BrowserPreferenceStore implements PreferenceStore {
  private memory: StoredPreferences | null = null;

  constructor(
    private readonly key = DEFAULT_PREFERENCE_KEY,
    private readonly storage: Storage | null =
      typeof globalThis.localStorage === "undefined" ? null : globalThis.localStorage,
  ) {}

  read(): StoredPreferences | null {
    if (!this.storage) return this.memory;
    let raw: string | null;
    try {
      raw = this.storage.getItem(this.key);
    } catch {
      return this.memory;
    }
    if (raw === null) return this.memory;
    try {
      const value: unknown = JSON.parse(raw);
      if (!isStoredPreferences(value)) {
        this.memory = null;
        return null;
      }
      this.memory = value;
      return value;
    } catch {
      this.memory = null;
      return null;
    }
  }

  write(value: StoredPreferences): boolean {
    this.memory = value;
    if (!this.storage) return false;
    try {
      this.storage.setItem(this.key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  }

  subscribe(listener: () => void): () => void {
    if (typeof globalThis.addEventListener !== "function") return () => undefined;
    const handler = (event: StorageEvent) => {
      if (event.key === this.key) listener();
    };
    globalThis.addEventListener("storage", handler);
    return () => globalThis.removeEventListener("storage", handler);
  }
}

export class MemoryPreferenceStore implements PreferenceStore {
  value: StoredPreferences | null = null;
  durable = true;
  private readonly listeners = new Set<() => void>();

  read(): StoredPreferences | null {
    return this.value;
  }

  write(value: StoredPreferences): boolean {
    this.value = structuredClone(value);
    for (const listener of this.listeners) listener();
    return this.durable;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
